/**
 * Document model for the downloadable (emailable) Family Banking Commitment
 * Agreement™ PDF.
 *
 * Pure data → strings. The jsPDF renderer draws whatever this model says, so
 * all interpolation and formatting decisions live here where they are unit
 * tested. The rich print report (charts, stewardship, Future Me letter)
 * remains the HTML document; this PDF is the concise, attachable artifact:
 * parties, terms, the full payment schedule, and signatures.
 */
import type { PaymentFrequency } from "@/lib/calculationEngine";
import { buildExportFilename } from "@/lib/loanId";

export interface CommitmentDocInput {
  loanId: string;
  policyOwner: string;
  loanBorrower: string;
  advisorName?: string;
  jurisdiction: "canada" | "usa";
  /** Total borrowed, including any consolidated existing balance. */
  loanAmount: number;
  /** Display rate in percent, e.g. 6.5. */
  annualRatePct: number;
  paymentFrequency: PaymentFrequency;
  loanDate: string;
  startDate: string;
  generatedOn: string; // ISO date
  schedule: { date: string; amount: number }[];
}

export interface CommitmentDocModel {
  filename: string;
  title: string;
  subtitle: string;
  parties: { label: string; value: string }[];
  facts: { label: string; value: string }[];
  commitments: string[];
  schedule: {
    head: string[];
    rows: string[][];
    totalLabel: string;
    totalValue: string;
  };
  signatures: { role: string; name: string }[];
  disclaimer: string;
}

const FREQUENCY_LABEL: Record<PaymentFrequency, string> = {
  weekly: "Weekly",
  biweekly: "Every two weeks",
  semimonthly: "Twice a month",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semiannual: "Twice a year",
  annual: "Annually",
  endofterm: "Single payment at end of term",
};

function money(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Trim trailing zeros: 6.5, not 6.50; 7, not 7.00. */
function pct(n: number): string {
  return `${Number(n.toFixed(2))}%`;
}

export function buildCommitmentDocModel(input: CommitmentDocInput): CommitmentDocModel {
  const paymentAmount = input.schedule[0]?.amount ?? 0;
  const lastDate = input.schedule[input.schedule.length - 1]?.date ?? null;
  const total = input.schedule.reduce((s, r) => s + r.amount, 0);
  const owner = input.policyOwner.trim() || "Policy Owner";
  const borrower = input.loanBorrower.trim() || "Borrower";

  return {
    filename: `${buildExportFilename(input.loanId, input.loanDate, input.loanAmount)}.pdf`,
    title: "Family Banking Commitment Agreement™",
    subtitle: `Loan ${input.loanId} · Generated ${input.generatedOn}`,
    parties: [
      { label: "Policy Owner (Lender)", value: owner },
      { label: "Borrower", value: borrower },
      ...(input.advisorName ? [{ label: "Advisor", value: input.advisorName }] : []),
      { label: "Jurisdiction", value: input.jurisdiction === "canada" ? "Canada" : "United States" },
    ],
    facts: [
      { label: "Loan amount", value: money(input.loanAmount) },
      { label: "Interest rate", value: `${pct(input.annualRatePct)} annually` },
      { label: "Payment frequency", value: FREQUENCY_LABEL[input.paymentFrequency] },
      { label: "Payment amount", value: paymentAmount > 0 ? money(paymentAmount) : "—" },
      { label: "Number of payments", value: String(input.schedule.length) },
      { label: "Loan date", value: input.loanDate },
      { label: "First payment", value: input.schedule[0]?.date ?? input.startDate },
      { label: "Final payment", value: lastDate ?? "—" },
    ],
    commitments: [
      `${borrower} commits to repaying this family loan of ${money(input.loanAmount)} according to the attached schedule of ${paymentAmount > 0 ? money(paymentAmount) : "planned"} payments.`,
      `Payments are made to the family bank — repaid dollars restore the family's lending capacity for the next need.`,
      `If a payment cannot be made as planned, ${borrower} agrees to tell ${owner} BEFORE the due date and record what actually happened, so the plan stays honest.`,
      `Both parties agree to review actual repayment against this schedule together, at least once per year.`,
    ],
    schedule: {
      head: ["#", "Due date", "Amount"],
      rows: input.schedule.map((r, i) => [String(i + 1), r.date, money(r.amount)]),
      totalLabel: "Total planned repayment",
      totalValue: money(total),
    },
    signatures: [
      { role: "Policy Owner (Lender)", name: owner },
      { role: "Borrower", name: borrower },
      ...(input.advisorName ? [{ role: "Advisor (Witness)", name: input.advisorName }] : []),
    ],
    disclaimer:
      "This document records a private family commitment. It is a planning and accountability tool, not banking, tax or legal advice; figures are projections based on the plan's assumptions. For a loan intended to be legally enforceable, consult a qualified professional in your jurisdiction.",
  };
}
